import React, { useMemo, useCallback } from 'react';
import PropTypes from 'prop-types';
import useModalOutsideClick from '../../hooks/useModalOutsideClick';
import './ViewEntryModal.css';
import { formatNumber } from '../../../../utils/helpers';

// Helper function to determine sport-specific metric details
const getMetricDetails = (sport) => {
  switch (sport) {
    case 'Golf':
      return { key: 'score', header: 'Score', formatPrefix: '' }; // Formatting handled in formatMetricValue
    case 'Chess':
      return { key: 'rating', header: 'Rating', formatPrefix: '' }; // Placeholder
    case 'Esports':
      return { key: 'kd', header: 'K/D', formatPrefix: '' }; // Placeholder
    case 'Poker':
    default:
      return { key: 'cashedFor', header: 'Cashes', formatPrefix: '$' };
  }
};

// Helper function to format the metric value based on sport
const formatMetricValue = (value, sport) => {
  if (value === null || value === undefined) return '-';
  switch (sport) {
    case 'Poker':
      return typeof value === 'number' ? `$${formatNumber(value)}` : '$0';
    case 'Golf':
      if (typeof value !== 'number') return '-';
      if (value === 0) return 'E';
      return value > 0 ? `+${value}` : value.toString();
    case 'Chess':
      return typeof value === 'number' ? formatNumber(value) : '-';
    case 'Esports':
      // Assuming K/D might be a string like '1.5' or '10/5'
      return typeof value === 'string' || typeof value === 'number' ? value.toString() : '-'; 
    default:
      return value.toString();
  }
};

const ViewEntryModal = ({
  show,
  onClose,
  onWithdraw,
  entry,
  contest,
  isWithdrawing,
  isSuccess,
}) => {
  // Call Hooks at the top level
  useModalOutsideClick(show, onClose, "overlay");

  // Determine metric key and header based on sport
  const { key: metricKey, header: metricHeader } = useMemo(() => getMetricDetails(contest?.sport), [contest?.sport]);

  // Player Status/Data Map
  const playerInfoMap = useMemo(() => {
    const map = {};
    if (contest?.players) {
      contest.players.forEach(player => {
        let metricValue = null;
        // Extract the correct metric based on the key
        if (metricKey === 'cashedFor') {
          metricValue = player.cashedFor ?? 0;
        } else if (metricKey === 'score') {
          metricValue = player.sportSpecificData?.score ?? null;
        } else if (metricKey === 'rating') {
          metricValue = player.sportSpecificData?.rating ?? null; // Placeholder
        } else if (metricKey === 'kd') {
          metricValue = player.sportSpecificData?.kd ?? null; // Placeholder
        }

        map[player.name] = {
          finish: player.finish ?? 0,
          salary: player.salary ?? 0,
          metricValue: metricValue // Store the dynamically determined metric
        };
      });
    }
    return map;
  }, [contest?.players, metricKey]);

  // Get Player Info (using the map) - wrap in useCallback to avoid recreating on each render
  const getPlayerInfo = useCallback((playerName) => {
    return playerInfoMap[playerName] || { finish: 0, salary: 0, metricValue: null };
  }, [playerInfoMap]);

  // --- Calculate Info Before Early Return --- 
  // Use optional chaining and provide defaults
  const captainName = entry?.team?.captain;
  const flexPlayerNames = entry?.team?.flexPlayers || [];

  // Memoize calculated info, ensuring dependencies include underlying data sources and functions
  const captainInfo = useMemo(() => getPlayerInfo(captainName), [captainName, getPlayerInfo]);
  const flexPlayerInfo = useMemo(() => flexPlayerNames.map(getPlayerInfo), [flexPlayerNames, getPlayerInfo]);

  // Calculate Total Metric Value (Captain bonus specific to Poker/Cashes)
  const totalMetricValue = useMemo(() => {
    let total = 0;
    let canSum = true;
    const captainValue = captainInfo?.metricValue;

    if (typeof captainValue === 'number') {
      // Apply 2x bonus ONLY for Poker cashes
      total += captainValue;
    } else if (captainValue !== null) {
      canSum = false; // Cannot sum non-numeric unless it's null
    }

    flexPlayerInfo.forEach(info => {
      const flexValue = info?.metricValue;
      if (typeof flexValue === 'number') {
        total += flexValue;
      } else if (flexValue !== null) {
        canSum = false;
      }
    });
    
    // Return total if summable, handle edge case where all values were null (show 0)
    if (!canSum) return null; // Indicates non-summable metric like K/D
    // If summing was possible, return the total (will be 0 if all players had null/0)
    return total; 
  }, [captainInfo, flexPlayerInfo, metricKey]);

  // Early return if not showing or no entry data
  if (!show || !entry) return null;

  // Now safe to destructure entry - team already accessed safely above
  const { team, teamSalary } = entry;

  // --- NEW: Add a check for Snake Drafts without a team yet ---
  if (!team) {
    return (
      <div className="overlay">
        <div className="confirm-dialog">
          <div className="dialog-header">
            <h2>Withdraw/View Entry: {entry.entryName}</h2>
            <button className="close-button" onClick={onClose}>×</button>
          </div>
          <div className="dialog-content">
            <p>You have entered this snake draft.</p>
            <p>Your team will be drafted when the contest begins.</p>
          </div>
          <div className="dialog-actions">
            {contest.status === 'Registering' && (
              <button 
                className="cancel-btn"
                onClick={onWithdraw}
                disabled={isWithdrawing || isSuccess}
              >
                {isSuccess ? 'Withdrawn' : isWithdrawing ? 'Withdrawing...' : 'Confirm Withdrawal'}
              </button>
            )}
            <button className="confirm-btn" onClick={onClose}>Close</button>
          </div>
        </div>
      </div>
    );
  }

  // Helper function to check if a player is active (only applies if contest is Running)
  const isPlayerActive = (playerName) => {
    // Add check for contest status
    if (!contest || contest.status !== 'Running') return true; 
    const info = playerInfoMap[playerName];
    
    // Sport-specific active check
    if (contest.sport === 'Golf') {
      // For Golf, check if player is NOT cut based on status
      const playerData = contest.players.find(p => p.name === playerName);
      return playerData?.sportSpecificData?.status !== 'cut';
    } else if (contest.sport === 'Poker') {
      // For Poker, eliminated if finish is > 0 OR -1 (pre-money bust).
      // Active if finish is 0 or null.
      return info?.finish === 0 || info?.finish === null; 
    } else {
      // For Poker and other sports, use finish property for active status
      return info?.finish === 0 || info?.finish === null; 
    }
  };

  return (
    <div className="overlay">
      <div className="confirm-dialog">
        <div className="dialog-header">
          <h2>Withdraw/View Entry: {entry.entryName}</h2>
          <button className="close-button" onClick={onClose}>
            ×
          </button>
        </div>

        <div className="dialog-content">
          <div className="contest-info">
            <h3>{contest.tournamentName}</h3>
            <div className="info-row">
              <span className="entry-fee-label">Entry Fee:</span>
              <span className="entry-fee-amount">${contest.entryFee + contest.adminFee}</span>
            </div>
          </div>

          <div className="lineup-preview">
            <h3>Lineup</h3>
            <table className="lineup-table">
              <thead>
                <tr>
                  <th>POS.</th>
                  <th>NAME</th>
                  <th>SALARY</th>
                  {/* Dynamic Header */}
                  <th>{metricHeader.toUpperCase()}</th> 
                </tr>
              </thead>
              <tbody>
                {(() => { // Captain Row
                  const isActive = isPlayerActive(team.captain);
                  const statusClass = contest.status === 'Running' ? (isActive ? 'player-active' : 'player-eliminated') : '';
                  // Apply 2x bonus only for Poker display
                  const displayValue = captainInfo.metricValue;
                  return (
                    <tr className={statusClass}>
                      <td data-position="Cpt" className="position-cpt">Cpt*</td>
                      <td className="player-name">
                        {team.captain}
                      </td>
                      <td className="salary">${formatNumber(captainInfo.salary)}</td>
                      {/* Dynamic Metric Cell */}
                      <td className="metric-value">{formatMetricValue(displayValue, contest.sport)}</td> 
                    </tr>
                  );
                })()}
                {team.flexPlayers.map((player, index) => { // Flex Player Rows
                  const isActive = isPlayerActive(player);
                  const statusClass = contest.status === 'Running' ? (isActive ? 'player-active' : 'player-eliminated') : '';
                  const playerInfo = flexPlayerInfo[index];
                  return (
                    <tr key={index} className={statusClass}>
                      <td data-position="Flex" className="position-flex">Flex</td>
                      <td className="player-name">
                        {player}
                      </td>
                      <td className="salary">${formatNumber(playerInfo.salary)}</td>
                       {/* Dynamic Metric Cell */}
                      <td className="metric-value">{formatMetricValue(playerInfo.metricValue, contest.sport)}</td> 
                    </tr>
                  );
                })}
                {/* Total Row */}
                <tr className="lineup-total-row">
                  <td colSpan="2">Total:</td> 
                  <td className="total-salary">${formatNumber(teamSalary)}</td>
                  {/* Dynamic Metric Total */}
                  <td className="total-metric">{formatMetricValue(totalMetricValue, contest.sport)}</td> 
                </tr>
              </tbody>
            </table>
          </div>

          {/* Sport-specific footer text */}
          {contest.sport === 'Poker' && (
            <div className="view-entry-modal-captain-bonus-info">
              *Captain's cashes count as tiebreaker.
            </div>
          )}
          {contest.sport === 'Golf' && (
            <div className="view-entry-modal-captain-bonus-info">
              *Captain's score counts as tiebreaker.
            </div>
          )}
          {(contest.sport === 'Chess' || contest.sport === 'Esports') && (
            <div className="view-entry-modal-captain-bonus-info">
              *Captain's performance contributes to your team results.
            </div>
          )}
        </div>

        <div className="dialog-actions">
          {/* Conditionally render buttons based on contest status */} 
          {contest.status === 'Registering' ? (
            <>
              <button 
                className="cancel-btn" // Note: Class name might be misleading, it's the Withdraw button
                onClick={onWithdraw}
                disabled={isWithdrawing || isSuccess}
              >
                {isSuccess ? (
                  <div className="withdraw-check">✓</div>
                ) : isWithdrawing ? (
                  <div className="withdraw-spinner"></div>
                ) : (
                  "Withdraw Entry"
                )}
              </button>
              <button 
                className="confirm-btn" // Note: Class name might be misleading, it's the Keep Entry button
                onClick={onClose}
              >
                Keep Entry
              </button>
            </>
          ) : (

             <button 
                className="confirm-btn" 
                onClick={onClose}
             >
                Close
             </button>
          )}
        </div>
      </div>
    </div>
  );
};

ViewEntryModal.propTypes = {
  show: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onWithdraw: PropTypes.func.isRequired,
  entry: PropTypes.shape({
    entryName: PropTypes.string,
    teamSalary: PropTypes.number, // This might include captain bonus depending on how it's calculated
    team: PropTypes.shape({
      captain: PropTypes.string,
      flexPlayers: PropTypes.arrayOf(PropTypes.string),
    }),
  }),
  contest: PropTypes.shape({
    tournamentName: PropTypes.string.isRequired,
    entryFee: PropTypes.number.isRequired,
    adminFee: PropTypes.number.isRequired,
    salaryCap: PropTypes.number.isRequired, // Added salaryCap prop type
    status: PropTypes.string.isRequired,
    sport: PropTypes.string.isRequired, // Added sport
    players: PropTypes.arrayOf(PropTypes.shape({
      name: PropTypes.string,
      salary: PropTypes.number,
      finish: PropTypes.number, // Used for active/eliminated status
      cashedFor: PropTypes.number, // Actual amount cashed for display
      sportSpecificData: PropTypes.shape({ // Added sportSpecificData
        score: PropTypes.number,
        rating: PropTypes.number, // Placeholder
        kd: PropTypes.string, // Placeholder (can be string like '1.5' or '10/5')
        status: PropTypes.string, // Added for Golf 'cut' status
        thru: PropTypes.oneOfType([PropTypes.number, PropTypes.string]), // Added for Golf
      })
    })).isRequired,
  }).isRequired,
  isWithdrawing: PropTypes.bool,
  isSuccess: PropTypes.bool,
};

ViewEntryModal.defaultProps = {
  isWithdrawing: false,
  isSuccess: false,
};

export default ViewEntryModal;
